import Button from "components/button";
import { Columns, Table } from "components/table";
import { VendorAddressFormValues, VendorAddressItem } from "_interfaces/vendor.interfaces";
import Icon from "components/icon";
import Input from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import { useCallback, useMemo, useState } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { NamespaceKeys } from "locale/types";
import useDebounce from "hooks/shared/useDebounce";
import VendorAccordion from "./vendor-accordion";
import {
  useStateServiceV1GetStatesByCountryIdQuery,
} from "_services/modules/master-data/states";
import {
  useCityServiceV1GetCityDropdownQuery,
} from "_services/modules/master-data/cities";
import { useCountryServiceV1ListCountriesQuery } from "_services/modules/master-data/countries";
import {
  addressCategoryOptions,
  getAvailableAddressCategoryOptions,
  getAvailableBranchOptions,
  getOptionLabel,
} from "data/address-category";

interface VendorAddressAccordionProps {
  isOpen: boolean;
  onToggle: () => void;
  onAddToList: () => void;
  onEdit: (index: number) => void;
  onDelete: (index: number) => void;
  addressItems: VendorAddressItem[];
  branchOptions: Array<{ label: string; value: string }>;
  hasBillingAddress: boolean;
  editingAddressIndex: number | null;
  form: UseFormReturn<VendorAddressFormValues>;
  t: (key: NamespaceKeys<"customer">, options?: Record<string, unknown>) => string;
}

type VendorAddressTableRow = VendorAddressItem & { rowIndex: number };

const VendorAddressAccordion = ({
  isOpen,
  onToggle,
  onAddToList,
  onEdit,
  onDelete,
  addressItems,
  branchOptions,
  hasBillingAddress,
  editingAddressIndex,
  form,
  t,
}: VendorAddressAccordionProps) => {
  const {
    register,
    formState: { errors },
  } = form;

  const { data: countriesData } = useCountryServiceV1ListCountriesQuery({ limit: 1000 });

  const countryOptions = useMemo(() => {
    return (
      countriesData?.countries?.map((c) => ({
        label: c.name ?? "",
        value: c.id ?? "",
      })) ?? []
    );
  }, [countriesData]);

  const selectedCountryId = useMemo(() => {
    const countryVal = form.watch("country");
    const matched = countryOptions.find((c) => c.label === countryVal || c.value === countryVal);
    return matched?.value ?? (countryVal || "102");
  }, [form.watch("country"), countryOptions]);

  const selectedProvinceId = form.watch("province");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const debouncedCitySearchTerm = useDebounce(citySearchTerm, 1000);

  const { data: statesData } = useStateServiceV1GetStatesByCountryIdQuery(
    {
      countryId: selectedCountryId || "102",
      limit: 1000,
    },
  );

  const { data: citiesData, isFetching: isFetchingCityOptions } = useCityServiceV1GetCityDropdownQuery(
    {
      countryId: selectedCountryId,
      stateId: selectedProvinceId || undefined,
      search: debouncedCitySearchTerm || undefined,
    },
    { skip: !selectedCountryId },
  );

  const provinceOptions = useMemo(() => {
    return (
      statesData?.states?.map((state) => ({
        label: state.name ?? "",
        value: state.id ?? "",
      })) ?? []
    );
  }, [statesData]);

  const cityOptions = useMemo(() => {
    return (
      citiesData?.cities?.map((city) => ({
        label: city.name ?? "",
        value: city.id ?? "",
      })) ?? []
    );
  }, [citiesData]);

  const handleSearchCity = useCallback(
    async (term: string) => {
      setCitySearchTerm(term);
      return cityOptions;
    },
    [cityOptions],
  );

  const availableCategoryOptions = useMemo(
    () => getAvailableAddressCategoryOptions(addressItems, editingAddressIndex),
    [addressItems, editingAddressIndex],
  );

  const availableBranchOptions = useMemo(
    () =>
      getAvailableBranchOptions(
        branchOptions.map((b) => ({ branchName: b.value })),
        addressItems,
        editingAddressIndex,
      ),
    [branchOptions, addressItems, editingAddressIndex],
  );

  const tableData = useMemo<VendorAddressTableRow[]>(
    () => addressItems.map((item, rowIndex) => ({ ...item, rowIndex })),
    [addressItems],
  );

  const columns = useMemo<Columns<VendorAddressTableRow>[]>(
    () => [
      {
        id: "addressName",
        fieldId: "addressName",
        label: t("col-address"),
        render: (row) => (
          <div>
            <div className="font-medium">{row.addressName}</div>
            {row.isBillingAddress ? (
              <div className="text-xs text-primary-700">{t("billing-address-tag")}</div>
            ) : null}
          </div>
        ),
      },
      {
        id: "addressCategory",
        fieldId: "addressCategory",
        label: t("col-category"),
        render: (row) => getOptionLabel(addressCategoryOptions, row.addressCategory),
      },
      {
        id: "branch",
        fieldId: "branch",
        label: t("col-branch"),
      },
      {
        id: "fullAddress",
        fieldId: "fullAddress",
        label: t("col-full-address"),
        render: (row) => <div className="max-w-[280px] truncate">{row.fullAddress}</div>,
      },
      {
        id: "country",
        fieldId: "country",
        label: t("col-country"),
        render: (row) => {
          const matched = countryOptions.find((c) => c.value === row.country || c.label === row.country);
          return row.countryName || matched?.label || (row.country === "102" ? "Indonesia" : row.country) || "Indonesia";
        },
      },
      {
        id: "province",
        fieldId: "province",
        label: t("col-province"),
        render: (row) => {
          const matched = provinceOptions.find((p) => p.value === row.province || p.label === row.province);
          return row.provinceName || matched?.label || row.province || "—";
        },
      },
      {
        id: "city",
        fieldId: "city",
        label: t("col-city"),
        render: (row) => {
          const matched = cityOptions.find((c) => c.value === row.city || c.label === row.city);
          return row.cityName || matched?.label || row.city || "—";
        },
      },
      {
        id: "action",
        fieldId: "rowIndex",
        label: t("col-action"),
        width: "120px",
        dragDisabled: true,
        render: (row) => (
          <div className="flex items-center gap-2" onClick={(event) => event.stopPropagation()}>
            <Button
              type="button"
              variant="outline"
              color="tertiary"
              size="small"
              iconLeft={<Icon name="edit-2" size={14} />}
              onClick={() => onEdit(row.rowIndex)}
            />
            <Button
              type="button"
              variant="outline"
              color="error"
              size="small"
              iconLeft={<Icon name="trash" size={14} />}
              onClick={() => onDelete(row.rowIndex)}
            />
          </div>
        ),
      },
    ],
    [onDelete, onEdit, countryOptions, provinceOptions, cityOptions, t],
  );

  return (
    <VendorAccordion
      title={t("accordion-vendor-address")}
      subtitle={t("accordion-vendor-address-subtitle")}
      iconName="location"
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className="flex flex-col gap-4">
        {!hasBillingAddress ? (
          <div className="rounded-lg bg-error-100 px-3 py-2 text-error-600 text-sm">
            {t("billing-required-alert")}
          </div>
        ) : null}

        <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-4">
          <Input
            label={t("label-address-name")}
            required
            placeholder={t("placeholder-address-name")}
            {...register("addressName")}
            error={errors.addressName}
          />

          <Controller
            name="addressCategory"
            control={form.control}
            render={({ field, fieldState }) => (
              <Select
                label={t("label-address-category")}
                required
                data-testid="select-branch-category"
                options={availableCategoryOptions}
                value={field.value}
                onValueChange={(value) => field.onChange(value)}
                error={fieldState.error}
                placeholder={t("placeholder-select")}
              />
            )}
          />

          <Controller
            name="branch"
            control={form.control}
            render={({ field, fieldState }) => (
              <Select
                label={t("label-branch")}
                required
                options={availableBranchOptions}
                value={field.value}
                onValueChange={(value) => field.onChange(value)}
                error={fieldState.error}
                placeholder={t("placeholder-select")}
              />
            )}
          />

          <Controller
            name="isBillingAddress"
            control={form.control}
            render={({ field }) => (
              <label className="flex h-full items-center justify-between px-3 py-2">
                <div>
                  <Typography variant="bodySmall" className="font-semibold">
                    {t("label-billing-address")}
                  </Typography>
                  <Typography variant="bodySmall" tone="muted">
                    {t("label-billing-address-helper")}
                  </Typography>
                </div>
                <button
                  type="button"
                  data-testid="checkbox-billing-address"
                  className={`ml-2 w-11 h-6 rounded-full p-1 transition-colors ${field.value ? "bg-primary-500" : "bg-greyScale-100"
                    }`}
                  onClick={() => field.onChange(!field.value)}
                >
                  <span
                    className={`block w-4 h-4 rounded-full bg-white transition-transform ${field.value ? "translate-x-5" : "translate-x-0"
                      }`}
                  />
                </button>
              </label>
            )}
          />

          <Controller
            name="country"
            control={form.control}
            render={({ field, fieldState }) => {
              const matched = countryOptions.find((c) => c.label === field.value || c.value === field.value);
              const selectValue = matched?.value ?? field.value;

              return (
                <Select
                  label={t("label-country")}
                  required
                  searchable
                  options={countryOptions}
                  value={selectValue}
                  onValueChange={(value) => {
                    field.onChange(value);
                    const selectedCountry = countryOptions.find((c) => c.value === value || c.label === value);
                    form.setValue("countryName", selectedCountry?.label || (value === "102" ? "Indonesia" : (value as string)));
                    form.setValue("province", "");
                    form.setValue("provinceName", "");
                    form.setValue("city", "");
                    form.setValue("cityName", "");
                  }}
                  error={fieldState.error}
                  placeholder={t("placeholder-select")}
                />
              );
            }}
          />

          <Controller
            name="province"
            control={form.control}
            render={({ field, fieldState }) => (
              <Select
                label={t("label-province")}
                required
                searchable
                disabled={!selectedCountryId}
                data-testid="select-branch-province"
                options={provinceOptions}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  const selectedProvince = provinceOptions.find((p) => p.value === value || p.label === value);
                  form.setValue("provinceName", selectedProvince?.label || "");
                  form.setValue("city", "");
                  form.setValue("cityName", "");
                }}
                error={fieldState.error}
                placeholder={t("placeholder-select")}
              />
            )}
          />

          <Controller
            name="city"
            control={form.control}
            render={({ field, fieldState }) => (
              <Select
                label={t("label-city")}
                required
                searchable
                loading={isFetchingCityOptions}
                disabled={!selectedCountryId}
                data-testid="select-branch-city"
                options={cityOptions}
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  const selectedCity = cityOptions.find((c) => c.value === value || c.label === value);
                  form.setValue("cityName", selectedCity?.label || "");
                }}
                onSearch={handleSearchCity}
                error={fieldState.error}
                placeholder={t("placeholder-select")}
              />
            )}
          />

          <Input
            label={t("label-postal-code")}
            required
            placeholder={t("placeholder-postal-code")}
            data-testid="input-branch-postal-code"
            {...register("postalCode")}
            error={errors.postalCode}
          />

          <Input
            label={t("label-full-address")}
            required
            placeholder={t("placeholder-full-address")}
            data-testid="input-branch-address"
            {...register("fullAddress")}
            error={errors.fullAddress}
          />

          <Input
            label={t("label-google-maps-url")}
            required
            placeholder={t("placeholder-google-maps-url")}
            {...register("mapsUrl")}
            error={errors.mapsUrl}
          />

          <div className="flex items-end col-span-2 justify-end">
            <Button
              type="button"
              variant="fill"
              color="primary"
              onClick={onAddToList}
            >
              {editingAddressIndex !== null ? t("btn-save-changes") : t("btn-add-to-list")}
            </Button>
          </div>
        </div>

        <div className="max-h-[320px] overflow-auto rounded-xl">
          <Table<VendorAddressTableRow>
            data={tableData}
            columns={columns}
            rowKey="rowIndex"
            currentPage={1}
            limit={10}
          />
        </div>
      </div>
    </VendorAccordion>
  );
};

export default VendorAddressAccordion;
